<template>
  <div>
    <pdf-preview
      ref="pdfPreview"
      :saving="saving"
      :formData="formData"
      @in-preview="inPreview = $event"
      @save="save"
      @save-preview="save(true)"
    />
    <div v-show="!inPreview">
      <form autocomplete="off" @submit.prevent>
        <h2>FORMULARIO SOLICITUD DE COMETIDO FUNCIONARIO</h2>
        <div class="row mt-10">
          <div class="col-12">
            <b-form-group>
              <label for="previousDocument">
                ¿Deseas basarte en otro documento?
              </label>
              <v-select
                v-model="previousDocument"
                :options="createdDocuments"
                name="previousDocument"
                label="label"
                data-cy="created-document-data"
                @input="getDocumentData($event)"
                :clearable="true"
              >
                <template slot="no-options">
                  no hay documentos registrados
                </template>
              </v-select>
            </b-form-group>
          </div>
        </div>
        <div class="row mt-10">
          <div class="col-12">
            <h4>IDENTIFICACIÓN DEL SOLICITANTE</h4>
          </div>
          <div class="col-6">
            <validate-field
              field="requesterName"
              label="Usuario solicitante"
              v-slot="{ invalid }"
            >
              <ListUser
                class="w-100 mb-0"
                autocomplete
                label=""
                name="requesterName"
                placeholder="Ingrese el nombre del solicitante"
                v-model="formData.requester"
                data-vv-as="solicitante"
                data-cy="requester-name"
                v-validate="'required'"
                :state="invalid"
                @full-user="setRequesterData"
              />
              Se completa automáticamente. Verifica que tu nombre esté correcto.
            </validate-field>
          </div>
          <div class="col-6">
            <validate-field
              field="requesterIut"
              label="Rut usuario solicitante"
              v-slot="{ invalid }"
            >
              <b-input
                name="requesterIut"
                placeholder="Ingrese el rut del nombre del solicitante"
                v-model="formData.requesterIut"
                data-vv-as="rut solicitante"
                data-cy="requester-iut"
                v-validate="'required'"
                :state="invalid"
              />
              Se completa automáticamente. Revisa que el número esté correcto.
            </validate-field>
          </div>
          <div class="col-6">
            <validate-field
              field="requestDate"
              label="Fecha de solicitud"
              v-slot="{ invalid }"
            >
              <date-picker
                name="requestDate"
                v-model="formData.requestDate"
                placeholder="Seleccione la fecha de solicitud"
                data-vv-as="fecha de solicitud"
                data-cy="request-date"
                format="YYYY-MM-DD"
                v-validate="'required'"
                :invalid="invalid"
                @formatted-input="formData.requestDate = $event"
                :disablePastDates="true"
              />
              Fecha en que ingresas la solicitud. El sistema la carga automáticamente.
            </validate-field>
          </div>
          <div class="col-6">
            <validate-field
              field="requesterUnit"
              label="Unidad requirente"
              v-slot="{ invalid }"
            >
              <select-options
                name="requesterUnit"
                :alphabetical-sort="true"
                options-group="fisabs-requester-unit"
                v-model="formData.requesterUnit"
                data-vv-as="unidad requirente"
                data-cy="requester-unit"
                v-validate="'required'"
                :invalid="invalid"
              />
              Selecciona la unidad a la que perteneces según el organigrama institucional.
            </validate-field>
          </div>
          <div class="col-6">
            <validate-field
              field="requesterPosition"
              label="Cargo del solicitante"
              v-slot="{ invalid }"
            >
              <b-input
                name="requesterPosition"
                placeholder="Ingrese el cargo del solicitante"
                v-model="formData.requesterPosition"
                data-vv-as="cargo del solicitante"
                data-cy="requester-position"
                v-validate="'required'"
                :state="invalid"
              />
              Ingresa tu cargo actual tal como aparece en tu designación o convenio.
            </validate-field>
          </div>
          <div class="col-6">
            <validate-field
              field="requesterLegalStatus"
              label="Calidad Jurídica del solicitante"
              v-slot="{ invalid }"
            >
              <select-options
                name="requesterLegalStatus"
                :alphabetical-sort="true"
                options-group="calidad-juridica"
                v-model="formData.requesterLegalStatus"
                data-vv-as="calidad Jurídica del solicitante"
                data-cy="requester-legal-status"
                v-validate="'required'"
                :invalid="invalid"
                @input="onRequesterLegalStatusChange"
              />
              Selecciona tu condición contractual: Planta, Contrata u Honorarios.
            </validate-field>
          </div>
          <div class="col-6" v-if="shouldShowRequesterGrade">
            <validate-field
              field="requesterGrade"
              label="Grado del solicitante"
              v-slot="{ invalid }"
            >
              <b-input
                name="requesterGrade"
                placeholder="Ingrese el grado del solicitante"
                v-model="formData.requesterGrade"
                data-vv-as="grado del solicitante"
                data-cy="requester-grade"
                v-validate="{ required: shouldShowRequesterGrade }"
                :state="invalid"
              />
              Grado actual, según el registro vigente en Gestión de Personas.
            </validate-field>
          </div>
          <div class="col-12">
            <h4>INFORMACIÓN SOBRE EL COMETIDO Y LAS ACTIVIDADES</h4>
          </div>
          <div class="col-12">
            <h6 class="text-primary">
              Periodo del cometido
            </h6>
          </div>

          <div class="col-12 mb-2">
            Importante: las fechas que se ingresen en este campo,
            correspondientes al cometido, deben ajustarse a lo
            establecido en la normativa vigente de la Universidad.
          </div>
          <div
            v-for="(period, idx) in formData.periods"
            :key="`period-${idx}`"
            class="col-12"
          >
            <div class="row">
            <div class="col-12" v-if="formData.periods.length > 1">
              <h6 class="text-primary">
                Periodo {{ idx + 1 }}
              </h6>
            </div>

            <div class="col-6">
              <validate-field
                :field="`startDate_${idx}`"
                label="Fecha de inicio"
                v-slot="{ invalid }"
              >
                <input
                  type="hidden"
                  :name="`startDate_${idx}`"
                  :data-vv-name="`startDate_${idx}`"
                  data-vv-as="fecha de inicio"
                  v-model="period.startDate"
                  v-validate="'required'"
                />
                <date-picker
                  :name="`startDate_${idx}`"
                  :data-vv-name="`startDate_${idx}`"
                  v-model="period.startDate"
                  placeholder="Seleccione la fecha de inicio"
                  data-vv-as="fecha de inicio"
                  :data-cy="`start-date-${idx}`"
                  type="datetime"
                  format="YYYY-MM-DD HH:mm"
                  value-type="YYYY-MM-DD HH:mm"
                  :invalid="invalid"
                  @formatted-input="onPeriodStartDateInput($event, idx)"
                  :disablePastDates="true"
                />
              </validate-field>
            </div>

            <div class="col-6">
              <validate-field
                :field="`endDate_${idx}`"
                label="Fecha de término"
                v-slot="{ invalid }"
              >
                <date-picker
                  :name="`endDate_${idx}`"
                  :data-vv-name="`endDate_${idx}`"
                  v-model="period.endDate"
                  placeholder="Seleccione la fecha de término"
                  data-vv-as="fecha de término"
                  :data-cy="`end-date-${idx}`"
                  type="datetime"
                  format="YYYY-MM-DD HH:mm"
                  v-validate="'required'"
                  :invalid="invalid"
                  @formatted-input="period.endDate = $event"
                  :disablePastDates="true"
                />
              </validate-field>
            </div>

            <div class="col-12 d-flex justify-content-end gap-2 mt-2">
              <b-button
                v-if="idx > 0"
                size="sm"
                variant="outline-danger"
                :data-cy="`period-remove-${idx}`"
                @click="removePeriodItem(idx)"
              >
                Quitar periodo
              </b-button>

              <b-button
                v-if="idx === formData.periods.length - 1"
                size="sm"
                variant="outline-primary"
                data-cy="period-add"
                @click="addPeriodItem"
              >
                + Agregar periodo
              </b-button>
            </div>

            <div class="col-12" v-if="idx < formData.periods.length - 1">
              <hr class="my-6" />
            </div>
          </div>
        </div>
          <div class="col-12">
            <h6 class="text-primary">
              Destino del cometido
            </h6>
          </div>
          <div
            v-for="(destination, idx) in formData.destinations"
            :key="`destination-${idx}`"
            class="col-12"
          >
            <div class="row">
              <div class="col-12" v-if="formData.destinations.length > 1">
                <h6 class="text-primary">
                  Destino {{ idx + 1 }}
                </h6>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`countryOfMission_${idx}`"
                  label="País de destino"
                  v-slot="{ invalid }"
                >
                  <input
                    type="hidden"
                    :name="`countryOfMission_${idx}`"
                    :data-vv-name="`countryOfMission_${idx}`"
                    data-vv-as="país de destino"
                    v-model="destination.countryOfMission"
                    v-validate="'required'"
                  />
                  <v-select
                    :name="`countryOfMission_${idx}`"
                    :alphabetical-sort="true"
                    v-model="destination.countryOfMission"
                    data-vv-as="país de destino"
                    :data-cy="`country-of-mission-${idx}`"
                    :invalid="invalid"
                    :class="{ 'v-select-danger': $validator.errors.has(`countryOfMission_${idx}`) }"
                    placeholder="Seleccione"
                    :state="$validator.errors.has(`countryOfMission_${idx}`) ? false : null"
                    :danger="$validator.errors.has(`countryOfMission_${idx}`)"
                    :danger-text="$validator.errors.first(`countryOfMission_${idx}`)"
                    :selectable="opt => !opt.disabled"
                    :options="countries"
                    :clearable="true"
                    @input="onDestinationCountryInput($event, idx); searchCitiesForDestination($event, idx)"
                  >
                    <template #spinner>
                      <b-spinner
                        small
                        v-if="loadingCountries"
                        :style="{ opacity: loadingCountries ? 1 : 0 }"
                        variant="primary"
                      />
                    </template>
                    <template slot="no-options">
                      <span v-if="!loadingCountries && !countries.length">
                        no hay opciones disponibles
                      </span>
                    </template>
                  </v-select>
                  Selecciona el país donde realizarás el cometido.
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`cityOfMission_${idx}`"
                  label="Ciudad de destino"
                  v-slot="{ invalid }"
                >
                  <v-select
                    :name="`cityOfMission_${idx}`"
                    :alphabetical-sort="true"
                    v-model="destination.cityOfMission"
                    data-vv-as="ciudad de destino"
                    :data-cy="`city-of-mission-${idx}`"
                    v-validate="'required'"
                    :invalid="invalid"
                    :class="{ 'v-select-danger': $validator.errors.has(`cityOfMission_${idx}`) }"
                    placeholder="Seleccione"
                    :state="$validator.errors.has(`cityOfMission_${idx}`) ? false : null"
                    :danger="$validator.errors.has(`cityOfMission_${idx}`)"
                    :danger-text="$validator.errors.first(`cityOfMission_${idx}`)"
                    :selectable="opt => !opt.disabled"
                    :options="citiesByDestination[idx] || []"
                    :clearable="true"
                    @input="setSelectedCityForDestination($event, idx)"
                  >
                    <template #spinner>
                      <b-spinner
                        small
                        v-if="loadingCitiesByDestination[idx]"
                        :style="{ opacity: loadingCitiesByDestination[idx] ? 1 : 0 }"
                        variant="primary"
                      />
                    </template>
                    <template slot="no-options">
                      <span v-if="!loadingCitiesByDestination[idx] && !(citiesByDestination[idx] && citiesByDestination[idx].length)">
                        no hay opciones disponibles
                      </span>
                    </template>
                  </v-select>
                  Elige la ciudad a la que viajarás durante el cometido.
                </validate-field>
              </div>

              <div class="col-12 d-flex justify-content-end gap-2 mt-2">
                <b-button
                  v-if="idx > 0"
                  size="sm"
                  variant="outline-danger"
                  :data-cy="`destination-remove-${idx}`"
                  @click="removeDestinationItem(idx)"
                >
                  Quitar destino
                </b-button>

                <b-button
                  v-if="idx === formData.destinations.length - 1"
                  size="sm"
                  variant="outline-primary"
                  data-cy="destination-add"
                  @click="addDestinationItem"
                >
                  + Agregar destino
                </b-button>
              </div>

              <div class="col-12" v-if="idx < formData.destinations.length - 1">
                <hr class="my-6" />
              </div>
            </div>
          </div>
          <div class="col-12">
            <h6 class="text-primary">
              Detalle de la actividad
            </h6>
          </div>

          <div class="col-12 mb-2">
            Importante: las fechas que se ingresen en este campo,
            correspondientes al inicio y término de la actividad deben
            estar debidamente respaldadas por medios de verificación válidos,
            de acuerdo con los criterios establecidos en la normativa universitaria.
          </div>
          <div
            v-for="(activity, idx) in formData.activities"
            :key="`activity-${idx}`"
            class="col-12"
          >
            <div class="row">
              <div class="col-12" v-if="formData.activities.length > 1">
                <h6 class="text-primary">
                  Actividad {{ idx + 1 }}
                </h6>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`activityStarDate_${idx}`"
                  label="Fecha de inicio actividad"
                  v-slot="{ invalid }"
                >
                  <input
                    type="hidden"
                    :name="`activityStarDate_${idx}`"
                    :data-vv-name="`activityStarDate_${idx}`"
                    data-vv-as="fecha de inicio de actividad"
                    v-model="activity.startDate"
                    v-validate="'required'"
                  />
                  <date-picker
                    :name="`activityStarDate_${idx}`"
                    v-model="activity.startDate"
                    placeholder="Seleccione la fecha de inicio de actividad"
                    data-vv-as="fecha de inicio de actividad"
                    :data-cy="`activity-start-date-${idx}`"
                    type="datetime"
                    format="YYYY-MM-DD HH:mm"
                    :invalid="invalid"
                    @formatted-input="onActivityStartDateInput($event, idx)"
                    :disablePastDates="true"
                  />
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`activityEndDate_${idx}`"
                  label="Fecha de término actividad"
                  v-slot="{ invalid }"
                >
                  <date-picker
                    :name="`activityEndDate_${idx}`"
                    v-model="activity.endDate"
                    placeholder="Seleccione la fecha de término de actividad"
                    data-vv-as="fecha de término de actividad"
                    :data-cy="`activity-end-date-${idx}`"
                    type="datetime"
                    format="YYYY-MM-DD HH:mm"
                    v-validate="'required'"
                    :invalid="invalid"
                    @formatted-input="activity.endDate = $event"
                    :disablePastDates="true"
                  />
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`activityName_${idx}`"
                  label="Nombre de actividad"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="`activityName_${idx}`"
                    placeholder="Ingrese el nombre de actividad"
                    v-model="activity.name"
                    data-vv-as="nombre de actividad"
                    :data-cy="`activity-name-${idx}`"
                    v-validate="'required'"
                    :state="invalid"
                  />
                  Indica el nombre oficial de la actividad.
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`activityDetail_${idx}`"
                  label="Detalle de actividad"
                  v-slot="{ invalid }"
                >
                  <b-form-textarea
                    :maxlength="2000"
                    rows="3"
                    max-rows="8"
                    :name="`activityDetail_${idx}`"
                    placeholder="Ingrese el detalle de actividad"
                    v-model="activity.detail"
                    data-vv-as="detalle de actividad"
                    :data-cy="`activity-detail-${idx}`"
                    v-validate="'required'"
                    :state="invalid"
                  />
                  <small class="form-text text-muted text-right">
                    {{ activity.detail ? activity.detail.length : 0 }} / 2000
                  </small>
                  Describe claramente qué actividad realizarás y su relación con tus funciones.
                </validate-field>
              </div>

              <div class="col-12 d-flex justify-content-end gap-2 mt-2">
                <b-button
                  v-if="idx > 0"
                  size="sm"
                  variant="outline-danger"
                  :data-cy="`activity-remove-${idx}`"
                  @click="removeActivityItem(idx)"
                >
                  Quitar actividad
                </b-button>

                <b-button
                  v-if="idx === formData.activities.length - 1"
                  size="sm"
                  variant="outline-primary"
                  data-cy="activity-add"
                  @click="addActivityItem"
                >
                  + Agregar actividad
                </b-button>
              </div>

              <div class="col-12" v-if="idx < formData.activities.length - 1">
                <hr class="my-6" />
              </div>
            </div>
          </div>
          <div class="col-12">
            <h4>INFORMACIÓN DEL TRASLADO</h4>
            <p>
              Debes ingresar cada tramo del viaje por separado. Un tramo corresponde
              a un traslado específico, por ejemplo: taxi al aeropuerto, vuelo,
              taxi desde el aeropuerto al hotel, y el retorno a tu lugar de origen.
              Cada uno de esos traslados debe registrarse como un tramo independiente
              e indicar si aplica o no reembolso.
            </p>
          </div>
          <div class="col-12">
            <h4>TRASLADO DE IDA</h4>
          </div>

          <div
            v-for="(segment, index) in formData.outboundSegments"
            :key="`outbound-segment-${index}`"
            class="col-12"
          >
            <div class="row">
              <div class="col-12" v-if="formData.outboundSegments.length > 1">
                <h6 class="text-primary mb-2">
                  Tramo de ida {{ index + 1 }}
                </h6>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`outboundSegments[${index}].transferNumber`"
                  label="N° de Tramo"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="`outboundSegments[${index}].transferNumber`"
                    placeholder="Ingrese el n° de Tramo"
                    v-model="segment.transferNumber"
                    data-vv-as="n° de Tramo"
                    :data-cy="`outbound-transfer-number-${index}`"
                    v-validate="'required'"
                    :state="invalid"
                  />
                  Indica el número de este tramo del viaje (por ejemplo: “Tramo 1”, “Tramo 2”).
                </validate-field>
              </div>

             <div class="col-6">
                <validate-field
                  :field="`outboundSegments[${index}].transferType`"
                  label="Medio de transporte"
                  v-slot="{ invalid }"
                >
                  <select-options
                    :name="`outboundSegments[${index}].transferType`"
                    :alphabetical-sort="true"
                    options-group="medio-transporte"
                    v-model="segment.transferType"
                    data-vv-as="medio de transporte"
                    :data-cy="`outbound-transfer-type-${index}`"
                    v-validate="'required'"
                    :invalid="invalid"
                    @input="onOutboundSegmentTransferTypeChange($event, index)"
                  />
                  Selecciona el medio de transporte utilizado en este tramo específico del viaje.
                </validate-field>
              </div>
              <div
                class="col-6"
                v-if="isTransferTypeOther(segment.transferType)"
              >
                <validate-field
                  :field="`outboundSegments[${index}].transferOtherDetail`"
                  label="Especificar medio de transporte"
                  v-slot="{ invalid }"
                >
                  <b-form-textarea
                    :name="`outboundSegments[${index}].transferOtherDetail`"
                    placeholder="Especifique el medio de transporte"
                    v-model="segment.transferOtherDetail"
                    data-vv-as="especificar medio de transporte"
                    :data-cy="`outbound-transfer-other-${index}`"
                    :maxlength="200"
                    rows="3"
                    max-rows="6"
                    v-validate="{ required: isTransferTypeOther(segment.transferType) }"
                    :state="invalid"
                  />
                  <small class="form-text text-muted text-right">
                    {{ segment.transferOtherDetail ? segment.transferOtherDetail.length : 0 }} / 200
                  </small>
                </validate-field>
              </div>
              <div
                class="col-6"
                v-if="shouldShowSegmentPlate(segment.transferType)"
              >
                <validate-field
                  :field="`outboundSegments[${index}].transferPlate`"
                  label="Patente"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="`outboundSegments[${index}].transferPlate`"
                    placeholder="Ingrese la patente"
                    v-model="segment.transferPlate"
                    data-vv-as="patente"
                    :data-cy="`outbound-transfer-plate-${index}`"
                    :maxlength="100"
                    v-validate="{ required: shouldShowSegmentPlate(segment.transferType) }"
                    :state="invalid"
                  />
                  Ingresa la patente del vehículo particular (máx. 100 caracteres).
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`outboundSegments[${index}].transferDate`"
                  label="Fecha"
                  v-slot="{ invalid }"
                >
                  <date-picker
                    :name="`outboundSegments[${index}].transferDate`"
                    v-model="segment.transferDate"
                    placeholder="Seleccione la fecha"
                    type="datetime"
                    format="YYYY-MM-DD HH:mm"
                    data-vv-as="fecha"
                    :data-cy="`outbound-transfer-date-${index}`"
                    v-validate="'required'"
                    :invalid="invalid"
                    @formatted-input="segment.transferDate = $event"
                    :disablePastDates="true"
                  />
                  Indica la fecha y hora de salida correspondiente a este tramo específico del viaje.
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`outboundSegments[${index}].transferOrigin`"
                  label="Origen"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="`outboundSegments[${index}].transferOrigin`"
                    placeholder="Ingrese origen"
                    v-model="segment.transferOrigin"
                    data-vv-as="origen"
                    :data-cy="`outbound-transfer-origin-${index}`"
                    v-validate="'required'"
                    :state="invalid"
                  />
                  Ingresa el lugar de inicio de este tramo específico del viaje. Puede ser una ciudad, aeropuerto,
                  terminal, dirección, etc. según corresponda.
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`outboundSegments[${index}].transferDestination`"
                  label="Destino"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="`outboundSegments[${index}].transferDestination`"
                    placeholder="Ingrese destino"
                    v-model="segment.transferDestination"
                    data-vv-as="destino"
                    :data-cy="`outbound-transfer-destination-${index}`"
                    v-validate="'required'"
                    :state="invalid"
                  />
                  Ingresa el lugar de destino de este tramo específico del viaje. Puede ser una ciudad, aeropuerto,
                  terminal, dirección, etc. según corresponda.
                </validate-field>
              </div>

              <div class="col-12">
                <b-form-group class="mt-0 mt-md-8 d-inline">
                  <label :for="`outboundRefundAppliesTrue-${index}`"> ¿Aplica reembolso?</label>
                  <div class="d-flex type-resend">
                    <b-form-radio
                      :id="`outboundRefundAppliesTrue-${index}`"
                      v-model="segment.refundApplies"
                      :value="true"
                      :name="`outboundRefundApplies-${index}`"
                      :data-cy="`outbound-refund-applies-${index}`"
                      data-vv-as="¿Aplica reembolso?"
                      @input="segment.justification = ''"
                    >
                      Si
                    </b-form-radio>
                    <b-form-radio
                      v-model="segment.refundApplies"
                      :name="`outboundRefundApplies-${index}`"
                      :data-cy="`outbound-refund-applies-${index}`"
                      data-vv-as="¿Aplica reembolso?"
                      :value="false"
                      @input="segment.justification = ''"
                      class="ml-4"
                    >
                      No
                    </b-form-radio>
                  </div>
                  Indica si los gastos de este tramo específico del viaje deberán ser reembolsados
                </b-form-group>
              </div>

              <div class="col-12" v-if="!segment.refundApplies">
                <validate-field
                  :field="`outboundSegments[${index}].justification`"
                  label="Justificación"
                  v-slot="{ invalid }"
                >
                  <b-form-textarea
                    :maxlength="2000"
                    rows="3"
                    max-rows="8"
                    v-validate="{ required: !segment.refundApplies }"
                    :name="`outboundSegments[${index}].justification`"
                    v-model="segment.justification"
                    data-vv-as="justificación"
                    :data-cy="`outbound-justification-${index}`"
                    :state="invalid"
                    counter="2000"
                    placeholder="Justificación"
                  />
                  <small class="form-text text-muted text-right">
                    {{ segment.justification ? segment.justification.length : 0 }} / 2000
                  </small>
                  Si selecciona NO, debe justificar su respuesta e indicar la fuente de financiamiento.
                </validate-field>
              </div>
              <div class="col-12 d-flex justify-content-end gap-2 mt-2">
                <b-button
                  v-if="index > 0"
                  size="sm"
                  variant="outline-danger"
                  :data-cy="`outbound-remove-${index}`"
                  @click="removeOutboundSegment(index)"
                >
                  Quitar tramo
                </b-button>

                <b-button
                  v-if="index === formData.outboundSegments.length - 1"
                  size="sm"
                  variant="outline-primary"
                  data-cy="outbound-add"
                  @click="addOutboundSegment"
                >
                  + Agregar tramo de ida
                </b-button>
              </div>
            </div>
          </div>
          <div class="col-12">
            <h4>TRASLADO DE REGRESO</h4>
          </div>

          <div
            v-for="(segment, index) in formData.returnSegments"
            :key="`return-segment-${index}`"
            class="col-12"
          >
            <div class="row">
              <div class="col-12" v-if="formData.returnSegments.length > 1">
                <h6 class="text-primary mb-2">
                  Tramo de regreso {{ index + 1 }}
                </h6>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`returnSegments[${index}].transferNumber`"
                  label="N° de Tramo"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="`returnSegments[${index}].transferNumber`"
                    placeholder="Ingrese el n° de Tramo"
                    v-model="segment.transferNumber"
                    data-vv-as="n° de Tramo"
                    :data-cy="`return-transfer-number-${index}`"
                    v-validate="'required'"
                    :state="invalid"
                  />
                  Indica el número de este tramo del viaje (por ejemplo: “Tramo 1”, “Tramo 2”).
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`returnSegments[${index}].transferType`"
                  label="Medio de transporte"
                  v-slot="{ invalid }"
                >
                  <select-options
                    :name="`returnSegments[${index}].transferType`"
                    :alphabetical-sort="true"
                    options-group="medio-transporte"
                    v-model="segment.transferType"
                    data-vv-as="medio de transporte"
                    :data-cy="`return-transfer-type-${index}`"
                    v-validate="'required'"
                    :invalid="invalid"
                    @input="onReturnSegmentTransferTypeChange($event, index)"
                  />
                  Selecciona el medio de transporte utilizado en este tramo específico del viaje.
                </validate-field>
              </div>
              <div
                class="col-6"
                v-if="isTransferTypeOther(segment.transferType)"
              >
                <validate-field
                  :field="`returnSegments[${index}].transferOtherDetail`"
                  label="Especificar medio de transporte"
                  v-slot="{ invalid }"
                >
                  <b-form-textarea
                    :name="`returnSegments[${index}].transferOtherDetail`"
                    placeholder="Especifique el medio de transporte"
                    v-model="segment.transferOtherDetail"
                    data-vv-as="especificar medio de transporte"
                    :data-cy="`return-transfer-other-${index}`"
                    :maxlength="200"
                    rows="3"
                    max-rows="6"
                    v-validate="{ required: isTransferTypeOther(segment.transferType) }"
                    :state="invalid"
                  />
                  <small class="form-text text-muted text-right">
                    {{ segment.transferOtherDetail ? segment.transferOtherDetail.length : 0 }} / 200
                  </small>
                </validate-field>
              </div>
              <div
                class="col-6"
                v-if="shouldShowSegmentPlate(segment.transferType)"
              >
                <validate-field
                  :field="`returnSegments[${index}].transferPlate`"
                  label="Patente"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="`returnSegments[${index}].transferPlate`"
                    placeholder="Ingrese la patente"
                    v-model="segment.transferPlate"
                    data-vv-as="patente"
                    :data-cy="`return-transfer-plate-${index}`"
                    :maxlength="100"
                    v-validate="{ required: shouldShowSegmentPlate(segment.transferType) }"
                    :state="invalid"
                  />
                  Ingresa la patente del vehículo particular (máx. 100 caracteres).
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`returnSegments[${index}].transferDate`"
                  label="Fecha"
                  v-slot="{ invalid }"
                >
                  <date-picker
                    :name="`returnSegments[${index}].transferDate`"
                    v-model="segment.transferDate"
                    placeholder="Seleccione la fecha"
                    data-vv-as="fecha"
                    type="datetime"
                    format="YYYY-MM-DD HH:mm"
                    :data-cy="`return-transfer-date-${index}`"
                    v-validate="'required'"
                    :invalid="invalid"
                    @formatted-input="segment.transferDate = $event"
                    :disablePastDates="true"
                  />
                  Indica la fecha y la hora de salida correspondiente a este tramo específico del viaje.
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`returnSegments[${index}].transferOrigin`"
                  label="Origen"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="`returnSegments[${index}].transferOrigin`"
                    placeholder="Ingrese origen"
                    v-model="segment.transferOrigin"
                    data-vv-as="origen"
                    :data-cy="`return-transfer-origin-${index}`"
                    v-validate="'required'"
                    :state="invalid"
                  />
                  Ingresa el lugar de inicio de este tramo específico del viaje. Puede ser una ciudad, aeropuerto,
                  terminal, dirección, etc. según corresponda.
                </validate-field>
              </div>

              <div class="col-6">
                <validate-field
                  :field="`returnSegments[${index}].transferDestination`"
                  label="Destino"
                  v-slot="{ invalid }"
                >
                  <b-input
                    :name="`returnSegments[${index}].transferDestination`"
                    placeholder="Ingrese destino"
                    v-model="segment.transferDestination"
                    data-vv-as="destino"
                    :data-cy="`return-transfer-destination-${index}`"
                    v-validate="'required'"
                    :state="invalid"
                  />
                  Ingresa el lugar de destino de este tramo específico del viaje. Puede ser una ciudad, aeropuerto,
                  terminal, dirección, etc. según corresponda.
                </validate-field>
              </div>

              <div class="col-12">
                <b-form-group class="mt-0 mt-md-8">
                  <label :for="`returnRefundAppliesTrue-${index}`">¿Aplica reembolso?</label>
                  <div class="d-flex type-resend">
                    <b-form-radio
                      :id="`returnRefundAppliesTrue-${index}`"
                      v-model="segment.refundApplies"
                      :value="true"
                      :name="`returnRefundApplies-${index}`"
                      :data-cy="`return-refund-applies-${index}`"
                      data-vv-as="¿Aplica reembolso?"
                      @input="segment.justification = ''"
                    >
                      Si
                    </b-form-radio>
                    <b-form-radio
                      :value="false"
                      :name="`returnRefundApplies-${index}`"
                      :data-cy="`return-refund-applies-${index}`"
                      data-vv-as="¿Aplica reembolso?"
                      v-model="segment.refundApplies"
                      @input="segment.justification = ''"
                      class="ml-4"
                    >
                      No
                    </b-form-radio>
                  </div>
                  Indica si los gastos de este tramo específico del viaje deberán ser reembolsados
                </b-form-group>
              </div>

              <div class="col-12" v-if="!segment.refundApplies">
                <validate-field
                  :field="`returnSegments[${index}].justification`"
                  label="Justificación"
                  v-slot="{ invalid }"
                >
                  <b-form-textarea
                    :name="`returnSegments[${index}].justification`"
                    v-model="segment.justification"
                    data-vv-as="justificación"
                    :data-cy="`return-justification-${index}`"
                    :state="invalid"
                    :maxlength="2000"
                    placeholder="Justificación"
                    rows="3"
                    v-validate="{ required: !segment.refundApplies }"
                    max-rows="8"
                  />
                  <small class="form-text text-muted text-right">
                    {{ segment.justification ? segment.justification.length : 0 }} / 2000
                  </small>
                  Si selecciona NO, debe justificar su respuesta e indicar la fuente de financiamiento.
                </validate-field>
              </div>
              <div class="col-12 d-flex justify-content-end gap-2 mt-2">
                <b-button
                  v-if="index > 0"
                  size="sm"
                  variant="outline-danger"
                  :data-cy="`return-remove-${index}`"
                  @click="removeReturnSegment(index)"
                >
                  Quitar tramo
                </b-button>

                <b-button
                  v-if="index === formData.returnSegments.length - 1"
                  size="sm"
                  variant="outline-primary"
                  data-cy="return-add"
                  @click="addReturnSegment"
                >
                  + Agregar tramo de regreso
                </b-button>
              </div>
            </div>
          </div>
          <div class="col-12">
            <h6>
              Justificación por compra de pasajes con menos de 7 días de anticipación
            </h6>
          </div>
          <div class="col-6">
            <b-form-group class="mt-0 mt-md-8">
              <b-form-checkbox
                name="travelMinorTo7days"
                data-cy="travel-minor-to-7days"
                data-vv-as="La solicitud del viaje es menor a 7 días de anticipación"
                v-model="formData.travelMinorTo7days"
                class="m-0"
              >
                La solicitud del viaje es menor a 7 días de anticipación
              </b-form-checkbox>
              Marca “Sí” si la fecha de inicio del viaje está a menos de 7 días desde hoy.
            </b-form-group>
          </div>
          <div class="col-12" v-if="formData.travelMinorTo7days">
            <h6 class="text-primary">
              Declaración de responsabilidad
            </h6>
            <p>
              La compra de pasaje y su fecha de ida es menor a lo exigido por DIPRES
              en cuanto buen uso de recursos públicos, en el que se indica que la
              fecha de compra debe ser como mínimo 7 días previo al vuelo, para
              continuar con la compra, debe ingresar justificación.
            </p>
          </div>
          <div class="col-12" v-if="formData.travelMinorTo7days">
            <h6 class="text-primary">
              Justificación
            </h6>
            <p>
              Selecciona el motivo que corresponde e indica brevemente la razón específica en el campo “Especificar”.
            </p>
          </div>
          <div class="col-6" v-if="formData.travelMinorTo7days">
            <validate-field
              field="shortTravelJustificationMotive"
              label="Motivo"
              v-slot="{ invalid }"
            >
              <select-options
                name="shortTravelJustificationMotive"
                :alphabetical-sort="true"
                options-group="motivo-viaje-menor"
                v-model="formData.shortTravelJustificationMotive"
                data-vv-as="motivo"
                data-cy="short-travel-justification-motive"
                v-validate="{required: formData.travelMinorTo7days}"
                :invalid="invalid"
              />
            </validate-field>
          </div>
          <div class="col-6" v-if="formData.travelMinorTo7days">
            <validate-field
              field="shortTravelJustification"
              label="Especificar"
              v-slot="{ invalid }"
            >
              <b-form-textarea
                name="shortTravelJustification"
                v-model="formData.shortTravelJustification"
                data-vv-as="especificar"
                data-cy="short-travel-justification"
                :state="invalid"
                :maxlength="2000"
                placeholder="Especificar"
                rows="3"
                v-validate="{required: formData.travelMinorTo7days}"
                max-rows="8"
              />
              <small class="form-text text-muted text-right">
                {{ formData.shortTravelJustification ? formData.shortTravelJustification.length : 0 }} / 2000
              </small>
            </validate-field>
          </div>
          <div class="col-12">
            <h4>ANTECEDENTES PRESUPUESTARIOS</h4>
          </div>
          <div class="col-6">
            <validate-field
              field="serviceCostCenter"
              label="Centro de Costos"
              v-slot="{ invalid }"
            >
              <select-options
                name="serviceCostCenter"
                :alphabetical-sort="true"
                options-group="fisabs-service-cost-center"
                v-model="formData.serviceCostCenter"
                data-vv-as="centro de costos"
                data-cy="service-cost-center"
                v-validate="'required'"
                :invalid="invalid"
              />
              <span>
                Selecciona el centro de costo que financiará el cometido.
              </span>
            </validate-field>
          </div>
          <div class="col-6">
            <validate-field
              field="serviceFundingSource"
              label="Fuente de financiamiento"
              v-slot="{ invalid }"
            >
              <select-options
                name="serviceFundingSource"
                options-group="fisabs-service-funding-source"
                v-model="formData.serviceFundingSource"
                data-vv-as="fuente de financiamiento"
                data-cy="service-funding-source"
                v-validate="'required'"
                :invalid="invalid"
              />
              <span>
                Indica la fuente que cubrirá los gastos.
              </span>
            </validate-field>
          </div>
          <div class="col-6">
            <b-form-group class="mt-0 mt-md-8">
              <b-form-checkbox
                name="isUryRed"
                data-cy="is-ury-red"
                data-vv-as="Es proyecto URY-RED"
                v-model="formData.isUryRed"
                class="m-0"
                @change="clearUryProjectData"
              >
                Es proyecto URY-RED
              </b-form-checkbox>
              Selecciona “Sí” solo si el cometido está asociado a un proyecto URY-RED.
            </b-form-group>
          </div>
          <div class="col-12" v-if="formData.isUryRed">
            <h4>
              Detalles técnicos (solo para proyectos URY-RED).
            </h4>
          </div>
          <div class="col-6" v-if="formData.isUryRed">
            <validate-field
              field="serviceInPurchasePlan"
              label="¿Está en Plan de Gastos? (Solo para Proyectos)"
              v-slot="{ invalid }"
            >
              <select-options
                name="serviceInPurchasePlan"
                v-model="formData.serviceInPurchasePlan"
                data-vv-as="indicar si está en plan de gastos"
                data-cy="service-in-purchase-plan"
                v-validate="'required'"
                :invalid="invalid"
                :options="options.yesOrNo"
                :mapper="opt => ({ value: opt, label: opt })"
                @input="formData.servicePurchasePlanJustification = ''"
              />
              Indica si el cometido aparece en el Plan de Gastos aprobado del proyecto.
            </validate-field>
          </div>
          <div class="col-6" v-if="formData.serviceInPurchasePlan === 'No' && formData.isUryRed">
            <validate-field
              field="servicePurchasePlanJustification"
              label="Justificación en caso de no estar en plan de gastos"
              v-slot="{ invalid }"
            >
              <b-form-textarea
                :maxlength="2000"
                rows="3"
                max-rows="8"
                name="servicePurchasePlanJustification"
                placeholder="Ingrese la justificación"
                v-model="formData.servicePurchasePlanJustification"
                data-vv-as="justificación en caso de no estar en plan de gastos"
                data-cy="service-purchase-plan-justification"
                v-validate="{required: formData.serviceInPurchasePlan === 'No'}"
                :state="invalid"
              />
              <small class="form-text text-muted text-right">
                {{ formData.servicePurchasePlanJustification ? formData.servicePurchasePlanJustification.length : 0 }} / 2000
              </small>
              Explica por qué esta actividad debe incorporarse ahora
              al Plan de Gastos y por qué no fue incluida originalmente.
            </validate-field>
          </div>
          <div class="col-6" v-if="formData.isUryRed">
            <validate-field
              field="serviceGlosa"
              label="Glosa"
            >
              <b-input
                name="serviceGlosa"
                placeholder="Ingrese la glosa"
                v-model="formData.serviceGlosa"
                data-cy="service-glosa"
              />
              (Nombre del proyecto cuando corresponda).
            </validate-field>
          </div>
          <div class="col-6" v-if="formData.isUryRed">
            <validate-field
              field="serviceExpenseClassification"
              label="Clasificación del gasto"
            >
              <select-options
                name="serviceExpenseClassification"
                options-group="fisabs-service-expense-classification"
                v-model="formData.serviceExpenseClassification"
                data-cy="service-expense-classification"
                data-vv-as="clasificación del gasto"
                v-validate="{required: formData.isUryRed}"
                :alphabetical-sort=true
              />
              (Asociado a movilización y mantención).
            </validate-field>
          </div>
          <div class="col-6" v-if="formData.isUryRed">
            <validate-field
              field="serviceProjectObjective"
              label="Objetivo del proyecto"
              v-slot="{ invalid }"
            >
              <b-form-textarea
                :maxlength="2000"
                rows="3"
                max-rows="8"
                v-validate="'required'"
                :invalid="invalid"
                name="serviceProjectObjective"
                data-vv-as="objetivo del proyecto"
                placeholder="Ingrese el objetivo del proyecto"
                v-model="formData.serviceProjectObjective"
                data-cy="service-project-objective"
              />
              <small class="form-text text-muted text-right">
                {{ formData.serviceProjectObjective ? formData.serviceProjectObjective.length : 0 }} / 2000
              </small>
            </validate-field>
          </div>
          <div class="col-6" v-if="formData.isUryRed">
            <validate-field
              field="serviceResult"
              label="Resultado / Hito / Actividad principal / Indicador"
              v-slot="{ invalid }"
            >
              <b-input
                v-validate="'required'"
                :invalid="invalid"
                name="serviceResult"
                data-vv-as="resultado/hito/actividad principal/indicador"
                placeholder="Ingrese el resultado/hito/actividad principal/indicador"
                v-model="formData.serviceResult"
                data-cy="service-result"
              />
            </validate-field>
          </div>
          <div class="col-6" v-if="formData.isUryRed">
            <validate-field
              field="serviceExpenseCode"
              label="Código del gasto en el plan de gastos del proyecto"
              v-slot="{ invalid }"
            >
              <b-input
                name="serviceExpenseCode"
                placeholder="Ingrese el código del gasto en el plan de gastos del proyecto"
                v-model="formData.serviceExpenseCode"
                data-cy="service-expense-code"
              />
              (En caso de incurrir en viáticos o movilización, ingresar ambos códigos).
            </validate-field>
          </div>
          <div class="col-12">
            <h4>
              INFORMACIÓN SOBRE LOS PASAJES AÉREOS
            </h4>
          </div>
          <div class="col-6">
            <b-form-group class="mt-0 mt-md-8">
              <b-form-checkbox
                name="isFlyingTicketIncluded"
                data-cy="is-flying-ticket-included"
                data-vv-as="Incluye compra de pasaje aéreo"
                v-model="formData.isFlyingTicketIncluded"
                @change="clearFlyingTicketData"
                class="m-0"
              >
                Incluye compra de pasaje aéreo
              </b-form-checkbox>
            </b-form-group>
          </div>
          <div class="col-12" v-if="formData.isFlyingTicketIncluded">
            <div class="row">
              <div class="col-12 text-primary">
                <h6>
                  Información del Solicitante para la compra de pasajes aéreos
                </h6>
              </div>
              <div class="col-6" v-if="formData.isFlyingTicketIncluded">
                <validate-field
                  field="flyingTicketBirthDate"
                  label="Fecha de nacimiento"
                  v-slot="{ invalid }"
                >
                  <date-picker
                    name="flyingTicketBirthDate"
                    v-model="formData.flyingTicketBirthDate"
                    placeholder="Seleccione la fecha de nacimiento"
                    data-vv-as="fecha de nacimiento"
                    format="YYYY-MM-DD"
                    value-type="YYYY-MM-DD"
                    data-cy="flying-ticket-birth-date"
                    v-validate="{ required: formData.isFlyingTicketIncluded}"
                    :invalid="invalid"
                    @formatted-input="formData.flyingTicketBirthDate = $event"
                  />
                </validate-field>
              </div>
              <div class="col-6" v-if="formData.isFlyingTicketIncluded">
                <validate-field
                  field="flyingTicketPhone"
                  label="Teléfono"
                  v-slot="{ invalid }"
                >
                  <b-input
                    name="flyingTicketPhone"
                    placeholder="Ingrese el teléfono"
                    data-vv-as="teléfono"
                    v-model="formData.flyingTicketPhone"
                    data-cy="flying-ticket-phone"
                    v-validate="{ required: formData.isFlyingTicketIncluded}"
                    :invalid="invalid"
                  />
                  Indica un número válido donde se te pueda contactar.
                </validate-field>
              </div>
              <div class="col-6" v-if="formData.isFlyingTicketIncluded">
                <validate-field
                  field="flyingTicketInstitutionalEmail"
                  label="Correo Institucional"
                  v-slot="{ invalid }"
                >
                  <b-input
                    name="flyingTicketInstitutionalEmail"
                    placeholder="Ingrese el correo institucional"
                    data-vv-as="correo institucional"
                    v-model="formData.flyingTicketInstitutionalEmail"
                    data-cy="flying-ticket-institutional-email"
                    v-validate="{ required: formData.isFlyingTicketIncluded}"
                    :invalid="invalid"
                  />
                  Escribe tu correo institucional vigente.
                </validate-field>
              </div>
              <div class="col-6" v-if="formData.isFlyingTicketIncluded">
                <validate-field
                  field="flyingTicketLuggage"
                  label="Equipaje"
                  v-slot="{ invalid }"
                >
                  <select-options
                    name="flyingTicketLuggage"
                    :alphabetical-sort="true"
                    options-group="tipos-equipaje"
                    v-model="formData.flyingTicketLuggage"
                    data-vv-as="equipaje"
                    multiple
                    data-cy="flying-ticket-luggage"
                    v-validate="{ required: formData.isFlyingTicketIncluded}"
                    :invalid="invalid"
                  />
                  Marca todos los tipos de equipaje que necesitarás para el vuelo.
                </validate-field>
              </div>
              <div class="col-6" v-if="needsJustificationLuggage">
                <validate-field
                  field="flyingTicketLuggageJustification"
                  label="Justificación"
                  v-slot="{ invalid }"
                >
                  <b-form-textarea
                    name="flyingTicketLuggageJustification"
                    v-model="formData.flyingTicketLuggageJustification"
                    :state="invalid"
                    :maxlength="2000"
                    placeholder="Justificación"
                    data-vv-as="justificación"
                    data-cy="flying-ticket-luggage-justification"
                    v-validate="'required'"
                    :invalid="invalid"
                    rows="3"
                    max-rows="8"
                  />
                  Explica por qué se requiere equipaje adicional, si aplica.
                </validate-field>
              </div>
              <div class="col-12">
                <h6>
                  Fechas Sugeridas para el Vuelo
                </h6>
                <p>
                  Ingresa las fechas y horarios sugeridos para tu vuelo de ida y regreso.
                  Estas fechas deben ser coherentes con el periodo del cometido, que debe
                  ajustarse a la normativa vigente de la Universidad, y con las fechas de
                  la actividad, las cuales deben estar respaldadas por medios de verificación
                  válidos. La emisión final del pasaje dependerá de la disponibilidad y de
                  la normativa vigente.
                </p>
                <p>
                  En caso de existir una diferencia justificada entre las fechas informadas
                  en el cometido y las consignadas en los pasajes aéreos efectivamente comprados,
                  y dicha diferencia implique ajustar el viático correspondiente, la
                  presente solicitud de cometido funcionario deberá ser modificada para
                  que ambas fechas queden debidamente alineadas.
                </p>
              </div>
              <div class="col-12">
                <h4>
                  IDA
                </h4>
              </div>
              <div class="col-6" v-if="formData.isFlyingTicketIncluded">
                <validate-field
                  field="flyingTicketDestinationDate"
                  label="Fecha sugerida"
                  v-slot="{ invalid }"
                >
                  <date-picker
                    name="flyingTicketDestinationDate"
                    v-model="formData.flyingTicketDestinationDate"
                    placeholder="Seleccione la fecha sugerida"
                    data-vv-as="fecha sugerida"
                    type="datetime"
                    format="YYYY-MM-DD HH:mm"
                    data-cy="flying-ticket-destination-date"
                    @formatted-input="formData.flyingTicketDestinationDate = $event"
                    :disablePastDates="true"
                    :invalid="invalid"
                  />
                </validate-field>
              </div>
              <div class="col-6" v-if="formData.isFlyingTicketIncluded">
                <validate-field
                  field="flyingTicketDestinationInfo"
                  label="Línea aérea y número de vuelo"
                  v-slot="{ invalid }"
                >
                  <b-input
                    name="flyingTicketDestinationInfo"
                    placeholder="Línea aérea y número de vuelo"
                    v-model="formData.flyingTicketDestinationInfo"
                    data-vv-as="origen"
                    data-cy="flying-ticket-destination-info"
                    :state="invalid"
                  />
                </validate-field>
              </div>
              <div class="col-6" v-if="formData.isFlyingTicketIncluded">
                <validate-field
                  field="flyingTicketDestinationComments"
                  label="Comentarios opcionales"
                  v-slot="{ invalid }"
                >
                  <b-input
                    name="flyingTicketDestinationComments"
                    placeholder="Comentarios opcionales"
                    v-model="formData.flyingTicketDestinationComments"
                    data-vv-as="comentarios opcionales"
                    data-cy="flying-ticket-destination-comments"
                    :state="invalid"
                  />
                </validate-field>
              </div>
              <div class="col-12">
                <h4>
                  REGRESO
                </h4>
              </div>
              <div class="col-6" v-if="formData.isFlyingTicketIncluded">
                <validate-field
                  field="flyingTicketReturnDate"
                  label="Fecha sugerida "
                  v-slot="{ invalid }"
                >
                  <date-picker
                    name="flyingTicketReturnDate"
                    v-model="formData.flyingTicketReturnDate"
                    placeholder="Seleccione la fecha sugerida"
                    data-vv-as="fecha sugerida"
                    type="datetime"
                    format="YYYY-MM-DD HH:mm"
                    data-cy="flying-ticket-return-date"
                    :invalid="invalid"
                    @formatted-input="formData.flyingTicketReturnDate = $event"
                    :disablePastDates="true"
                  />
                </validate-field>
              </div>
              <div class="col-6" v-if="formData.isFlyingTicketIncluded">
                <validate-field
                  field="flyingTicketReturnInfo"
                  label="Línea aérea y número de vuelo"
                  v-slot="{ invalid }"
                >
                  <b-input
                    name="flyingTicketReturnInfo"
                    placeholder="Línea aérea y número de vuelo"
                    v-model="formData.flyingTicketReturnInfo"
                    data-vv-as="origen"
                    data-cy="flying-ticket-return-info"
                    :state="invalid"
                  />
                </validate-field>
              </div>
              <div class="col-6" v-if="formData.isFlyingTicketIncluded">
                <validate-field
                  field="flyingTicketReturnComments"
                  label="Comentarios opcionales"
                  v-slot="{ invalid }"
                >
                  <b-input
                    name="flyingTicketReturnComments"
                    placeholder="Comentarios opcionales"
                    v-model="formData.flyingTicketReturnComments"
                    data-vv-as="comentarios opcionales"
                    data-cy="flying-ticket-return-comments"
                    :state="invalid"
                  />
                </validate-field>
              </div>
            </div>
          </div>
          <div class="col-12">
            <h6>
              Información adicional relevante
            </h6>
            <p>
              Utiliza este espacio para ingresar cualquier información extra
              que consideres relevante para la gestión del cometido y que no
              esté cubierta en los campos anteriores. Puedes incluir aclaraciones,
              condiciones especiales, antecedentes complementarios o cualquier detalle
              que facilite la revisión y aprobación de tu solicitud. Cuando corresponda,
              es recomendable acompañar medios de verificación que respalden la
              información ingresada.
            </p>
          </div>
          <div class="col-12">
            <validate-field
              field="additionalInformation"
              label="Información adicional"
              v-slot="{ invalid }"
            >
              <b-input
                name="additionalInformation"
                placeholder="Información adicional"
                v-model="formData.additionalInformation"
                data-vv-as="información adicional"
                data-cy="additionalInformation"
                :state="invalid"
              />
            </validate-field>
          </div>
          <vs-divider />
          <div class="col-12">
            <h4>Documentos</h4>
          </div>
          <div class="col-12">
            <dynamic-file-selector
              v-if="!scfAttachmentsSelectors.loading"
              :selectors="scfAttachmentsSelectors.list
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((sel) => ({ ...sel, ...sel.extra, index: sel.order }))
            "
              :object-to-evaluate="formData"
              :files="dynamicSelectorFiles"
              :reset-key="dfsResetKey"
              @update-files="updateFiles"
              @valid="(valid) => {
              dynamicFileSelectorValid = valid;
            }"
            />
            <div v-else>
              <b-skeleton animation="fade" width="100%"/>
              <b-skeleton animation="fade" width="100%"/>
            </div>
          </div>
        </div>
      </form>
      <form-buttons
        :saving="saving"
        :disabled="dfsSelectorsLoading"
        :back="() => this.$router.back()"
        :preview="preview"
        :save="() => save(false)"
        :saveDraft="saveDraft"
      />
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';

import ValidateField from '@/febos/global/customDocuments/components/ValidateField.vue';
import SelectOptions from '@/febos/global/customDocuments/components/SelectOptions.vue';
import PdfPreview from '@/febos/global/customDocuments/components/PdfPreview.vue';
import DatePicker from '@/febos/global/customDocuments/components/DatePicker';
import FormButtons from '@/febos/global/customDocuments/components/FormButtons';
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue';
import HandleDraft from '@/febos/global/documentManagement/mixins/HandleDraft';
import HandlePreLoadData from '@/febos/global/documentManagement/mixins/HandlePreLoadData';
import { showInvalidFormNotification } from '@/febos/global/customDocuments/utils/notifications';
import { scrollToNamedElement } from '@/febos/global/customDocuments/utils/scroll';
import { showToast } from '@/febos/global/customDocuments/utils/showToast';
import { ioCreateCustomDocument, ioGetCustomDocument } from '@/febos/servicios/api/customDocuments.api';
import ListUser from '@/febos/chile/lists/components/ListUser.vue';
import {listV2Options} from "@/febos/servicios/api/opciones.api";
import DynamicFileSelector
  from "@/febos/global/customDocuments/components/dynamic-file-selector/DynamicFileSelector.vue";

const DOC_TYPE = 'SCF';
const getTodayDate = () => {
  const today = new Date();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};
export default {
  name: DOC_TYPE,
  components: {
    DynamicFileSelector,
    ListUser,
    FormButtons,
    PdfPreview,
    SelectOptions,
    ValidateField,
    DatePicker,
    FeatherIcon
  },
  mixins: [HandleDraft, HandlePreLoadData],
  data() {
    return {
      draftKey: 0,
      usr: {},
      dynamicSelectorFiles: [],
      dynamicFileSelectorValid: false,
      formFiles: [],
      options: {
        yesOrNo: ['Sí', 'No'],
        serviceDeliveryType: ['Entrega inmediata', 'Servicio en el tiempo'],
        criteriaToSelectProvider: ['Precio', 'Menor plazo ofertado', 'Otro (detallar)'],
      },
      saving: false,
      inPreview: false,
      dfsValid: true,
      dfsSelectors: [],
      dfsSelectorsLoading: false,
      dfsFiles: [],
      countries: [],
      loadingCities: false,
      loadingCountries: false,
      cities: [],

      // Multi-destination: cities/options & loading state por índice
      loadingCitiesByDestination: {},
      citiesByDestination: {},
      formData: {
        requester: {},
        requesterName: '',
        requesterIut: '',
        requestDate: getTodayDate(),
        requesterUnit: '',
        requesterPosition: '',
        requesterLegalStatus: '',
        requesterGrade: '',

        // Legacy single mission period fields (se mantienen por compatibilidad / PDF)
        startDate: '',
        endDate: '',

        // Nueva estructura multi-period (ED-1867)
        periods: [
          {
            startDate: '',
            endDate: ''
          }
        ],

        // Legacy single destination fields (se mantienen por compatibilidad / PDF)
        countryOfMission: '',
        countrySelected: '',
        citySelected: '',
        cityOfMission: '',

        // Nueva estructura multi-destination (ED-1867)
        destinations: [
          {
            countryOfMission: '',
            countrySelected: '',
            cityOfMission: '',
            citySelected: ''
          }
        ],

        // Legacy single activity fields (se mantienen por compatibilidad / PDF)
        activityStarDate: '',
        activityEndDate: '',
        activityName: '',
        // Legacy single segment fields (maintained for backward compatibility)
        activityDetail: '',

        // Nueva estructura multi-activity (ED-1867)
        activities: [
          {
            startDate: '',
            endDate: '',
            name: '',
            detail: ''
          }
        ],

        destinationTransferNumber: '',
        destinationTransferDate: '',
        destinationTransferType: '',
        destinationTransferPlate: '',
        destinationTransferHour: '',
        destinationTransferOrigin: '',
        destinationTransferDestination: '',
        destinationRefundApplies: false,
        destinationJustification: '',
        returnTransferNumber: '',
        returnTransferDate: '',
        returnTransferType: '',
        returnTransferPlate: '',
        returnTransferHour: '',
        returnTransferOrigin: '',
        returnTransferDestination: '',
        returnRefundApplies: false,
        returnJustification: '',
        // New multi-segment arrays
        outboundSegments: [
          {
            transferNumber: '',
            transferType: '',
            transferOtherDetail: '',
            transferPlate: '',
            transferDate: '',
            transferOrigin: '',
            transferDestination: '',
            refundApplies: false,
            justification: ''
          }
        ],
        returnSegments: [
          {
            transferNumber: '',
            transferType: '',
            transferOtherDetail: '',
            transferPlate: '',
            transferDate: '',
            transferOrigin: '',
            transferDestination: '',
            refundApplies: false,
            justification: ''
          }
        ],
        travelMinorTo7days: false,
        shortTravelJustificationMotive: '',
        shortTravelJustification: '',
        serviceFundingSource: '',
        serviceCostCenter: '',
        isUryRed: false,
        serviceInPurchasePlan: '',
        servicePurchasePlanJustification: '',
        serviceGlosa: '',
        serviceExpenseClassification: '',
        serviceProjectObjective: '',
        serviceResult: '',
        serviceExpenseCode: '',
        isFlyingTicketIncluded: false,
        flyingTicketBirthDate: '',
        flyingTicketInstitutionalEmail: '',
        flyingTicketPhone: '',
        flyingTicketLuggage: '',
        flyingTicketLuggageSelected: [],
        flyingTicketLuggageJustification: '',
        flyingTicketDestinationDate: '',
        flyingTicketDestinationHour: '',
        flyingTicketDestinationInfo: '',
        flyingTicketDestinationComments: '',
        flyingTicketReturnDate: '',
        flyingTicketReturnHour: '',
        flyingTicketReturnInfo: '',
        flyingTicketReturnComments: '',
        additionalInformation: '',
      }
    };
  },
  computed: {
    ...mapGetters('Management', [
      'workerData'
    ]),
    ...mapGetters('List', [
      'scfAttachmentsSelectors',
    ]),
    ...mapGetters('Empresas', [
      'company',
    ]),
    getUserGrade() {
      return this.workerData.GRADO?.valor || '';
    },
    shouldShowRequesterGrade() {
      const status = (this.formData.requesterLegalStatus || '').toLowerCase();
      // Show grade only for Planta or Contrata
      return status === 'planta' || status === 'contrata';
    },
    needsJustificationLuggage() {
      const luggage = ['Maleta de bodega 27 kg', 'Maleta de bodega 23 kg', 'Equipaje especial'];
      return (this.formData.flyingTicketLuggage || []).some((item) => luggage.includes(item));
    },
    shouldShowDestinationTransferPlate() {
      return this.shouldShowSegmentPlate(this.formData.destinationTransferType);
    },
    shouldShowReturnTransferPlate() {
      return this.shouldShowSegmentPlate(this.formData.returnTransferType);
    },
  },
  methods: {
    ...mapActions('Management', [
      'getWorkerData',
      'setWorkerData'
    ]),
    ...mapActions('List', [
      'fetchScfAttachmentsSelectors',
    ]),
    updateFiles(files) {
      this.formFiles = [...files];
    },
    setSelectedCity(city) {
      this.formData.citySelected = city.label
    },
    async searchCities(country) {
      this.formData.countrySelected = country.label;
      try {
        this.loadingCities = true;
        const cities = await listV2Options({
          grupoOpcion: 'countries',
          itemsPorPagina: '20000',
          pagina: '1',
          filtros: `padre:${country.value}|deshabilitado:0`
        });
        this.loadingCities = false;
        if (cities) {
          this.cities = cities.data.opciones
            .map((item) => ({ label: item.descripcion, value: item.opcionId }))
            .sort((a, b) => a.label.localeCompare(b.label));
        }
      } catch (e) {
        console.log(e);
        this.loadingCities = false;
      }
    },
    getFormData() {
      // Partimos del payload base
      const formDataCopy = {
        ...this.formData,
        // Asegura arrays completos
        outboundSegments: (this.formData.outboundSegments || []).map((segment) => ({
          transferNumber: segment.transferNumber || '',
          transferType: segment.transferType || '',
          transferOtherDetail: segment.transferOtherDetail || '',
          transferPlate: segment.transferPlate || '',
          transferDate: segment.transferDate || '',
          transferOrigin: segment.transferOrigin || '',
          transferDestination: segment.transferDestination || '',
          refundApplies: !!segment.refundApplies,
          justification: segment.justification || ''
        })),
        returnSegments: (this.formData.returnSegments || []).map((segment) => ({
          transferNumber: segment.transferNumber || '',
          transferType: segment.transferType || '',
          transferOtherDetail: segment.transferOtherDetail || '',
          transferPlate: segment.transferPlate || '',
          transferDate: segment.transferDate || '',
          transferOrigin: segment.transferOrigin || '',
          transferDestination: segment.transferDestination || '',
          refundApplies: !!segment.refundApplies,
          justification: segment.justification || ''
        }))
      };

      // Sync legacy ED-1867 desde el primer item (pero al copy)
      const firstPeriod = this.formData.periods?.[0];
      if (firstPeriod) {
        formDataCopy.startDate = firstPeriod.startDate || '';
        formDataCopy.endDate = firstPeriod.endDate || '';
      }

      const firstDestination = this.formData.destinations?.[0];
      if (firstDestination) {
        formDataCopy.countryOfMission = firstDestination.countryOfMission || '';
        formDataCopy.cityOfMission = firstDestination.cityOfMission || '';
        formDataCopy.countrySelected = firstDestination.countrySelected || '';
        formDataCopy.citySelected = firstDestination.citySelected || '';
      }

      const firstActivity = this.formData.activities?.[0];
      if (firstActivity) {
        formDataCopy.activityStarDate = firstActivity.startDate || '';
        formDataCopy.activityEndDate = firstActivity.endDate || '';
        formDataCopy.activityName = firstActivity.name || '';
        formDataCopy.activityDetail = firstActivity.detail || '';
      }

      // Sync legacy traslados ED-1868 desde el primer segmento (al copy)
      const firstOutbound = this.formData.outboundSegments?.[0];
      if (firstOutbound) {
        formDataCopy.destinationTransferNumber = firstOutbound.transferNumber || '';
        formDataCopy.destinationTransferType = firstOutbound.transferType || '';
        formDataCopy.destinationTransferPlate = firstOutbound.transferPlate || '';
        formDataCopy.destinationTransferDate = firstOutbound.transferDate || '';
        formDataCopy.destinationTransferOrigin = firstOutbound.transferOrigin || '';
        formDataCopy.destinationTransferDestination = firstOutbound.transferDestination || '';
        formDataCopy.destinationRefundApplies = !!firstOutbound.refundApplies;
        formDataCopy.destinationJustification = firstOutbound.justification || '';
      }

      const firstReturn = this.formData.returnSegments?.[0];
      if (firstReturn) {
        formDataCopy.returnTransferNumber = firstReturn.transferNumber || '';
        formDataCopy.returnTransferType = firstReturn.transferType || '';
        formDataCopy.returnTransferPlate = firstReturn.transferPlate || '';
        formDataCopy.returnTransferDate = firstReturn.transferDate || '';
        formDataCopy.returnTransferOrigin = firstReturn.transferOrigin || '';
        formDataCopy.returnTransferDestination = firstReturn.transferDestination || '';
        formDataCopy.returnRefundApplies = !!firstReturn.refundApplies;
        formDataCopy.returnJustification = firstReturn.justification || '';
      }

      return formDataCopy;
    },
    addPeriodItem() {
      this.formData.periods.push({
        startDate: '',
        endDate: ''
      });
    },
    onPeriodStartDateInput(value, idx) {
      const period = this.formData.periods?.[idx];
      if (!period) {
        return;
      }

      period.startDate = value || '';

      // Revalidar inmediatamente para limpiar el error visual sin esperar a Preview
      this.$nextTick(() => {
        if (!this.$validator) {
          return;
        }

        this.$validator.validate(`startDate_${idx}`);
      });
    },
    addDestinationItem() {
      this.formData.destinations.push({
        countryOfMission: '',
        countrySelected: '',
        cityOfMission: '',
        citySelected: ''
      });
    },
    syncFirstPeriodToLegacy() {
      const first = this.formData.periods?.[0];
      if (!first) {
        return;
      }

      this.formData.startDate = first.startDate || '';
      this.formData.endDate = first.endDate || '';
    },
    syncFirstDestinationToLegacy() {
      const first = this.formData.destinations?.[0];
      if (!first) {
        return;
      }

      this.formData.countryOfMission = first.countryOfMission || '';
      this.formData.cityOfMission = first.cityOfMission || '';
      this.formData.countrySelected = first.countrySelected || '';
      this.formData.citySelected = first.citySelected || '';
    },
    async searchCitiesForDestination(country, idx) {
      const destination = this.formData.destinations?.[idx];
      if (!destination) {
        return;
      }

      // Set selected country label/value on this destination
      destination.countrySelected = country?.label || '';
      destination.countryOfMission = country || '';

      // Reset city on this destination
      destination.cityOfMission = '';
      destination.citySelected = '';

      // Clear old options for this destination
      this.$set(this.citiesByDestination, idx, []);

      if (!country || !country.value) {
        this.$set(this.loadingCitiesByDestination, idx, false);
        return;
      }

      try {
        this.$set(this.loadingCitiesByDestination, idx, true);

        const regions = await listV2Options({
          grupoOpcion: 'countries',
          itemsPorPagina: '20000',
          pagina: '1',
          filtros: `padre:${country.value}|deshabilitado:0`,
          return_child: 'Y'
        });

        // Extraer regiones y ciudades en un solo array
        const allOptions = (regions?.data?.opciones || []).reduce((acc, region) => {
          // Agregar la región
          const regionOption = {
            label: region.descripcion,
            value: region.opcionId
          };
          acc.push(regionOption);

          // Agregar las ciudades de esta región
          if (region.children && region.children.length > 0) {
            const regionCities = region.children.map((city) => ({
              label: city.descripcion,
              value: city.opcionId
            }));
            acc.push(...regionCities);
          }

          return acc;
        }, []);

        // Eliminar duplicados basándose en el nombre (label), manteniendo el primer ID encontrado
        const uniqueOptionsMap = new Map();
        allOptions.forEach((option) => {
          if (!uniqueOptionsMap.has(option.label)) {
            uniqueOptionsMap.set(option.label, option);
          }
        });

        const uniqueOptions = Array.from(uniqueOptionsMap.values());
        const options = uniqueOptions.sort((a, b) => a.label.localeCompare(b.label));

        this.$set(this.citiesByDestination, idx, options);
      } catch (e) {
        console.log(e);
        this.$set(this.citiesByDestination, idx, []);
      } finally {
        this.$set(this.loadingCitiesByDestination, idx, false);
      }
    },
    setSelectedCityForDestination(city, idx) {
      const destination = this.formData.destinations?.[idx];
      if (!destination) {
        return;
      }

      destination.citySelected = city?.label || '';
    },
    onDestinationCountryInput(value, idx) {
      const destination = this.formData.destinations?.[idx];
      if (!destination) {
        return;
      }

      destination.countryOfMission = value || '';

      this.$nextTick(() => {
        if (!this.$validator) {
          return;
        }

        this.$validator.validate(`countryOfMission_${idx}`);
      });
    },
    addActivityItem() {
      this.formData.activities.push({
        startDate: '',
        endDate: '',
        name: '',
        detail: ''
      });
    },
    onActivityStartDateInput(value, idx) {
      const activity = this.formData.activities?.[idx];
      if (!activity) {
        return;
      }

      activity.startDate = value || '';

      // Revalidar inmediatamente para limpiar el error visual sin esperar a Preview
      this.$nextTick(() => {
        if (!this.$validator) {
          return;
        }

        this.$validator.validate(`activityStarDate_${idx}`);
      });
    },
    syncFirstActivityToLegacy() {
      const first = this.formData.activities?.[0];
      if (!first) {
        return;
      }

      this.formData.activityStarDate = first.startDate || '';
      this.formData.activityEndDate = first.endDate || '';
      this.formData.activityName = first.name || '';
      this.formData.activityDetail = first.detail || '';
    },

    resetIndexedValidationErrors() {
      if (this.$validator && this.$validator.errors) {
        const errorFields = this.$validator.errors.items.map(e => e.field);
        errorFields.forEach(field => {
          if (
            /_(\d+)$/.test(field) &&
            (
              field.startsWith('activity') ||
              field.startsWith('startDate_') ||
              field.startsWith('endDate_') ||
              field.startsWith('countryOfMission_') ||
              field.startsWith('cityOfMission_')
            )
          ) {
            this.$validator.errors.remove(field);
          }
        });
      }
    },

    removeActivityItem(idx) {
      if (this.formData.activities.length <= 1) {
        return;
      }

      this.formData.activities.splice(idx, 1);
      this.resetIndexedValidationErrors();
    },

    removePeriodItem(idx) {
      if (this.formData.periods.length <= 1) {
        return;
      }

      this.formData.periods.splice(idx, 1);
      this.resetIndexedValidationErrors();
    },

    removeDestinationItem(idx) {
      if (this.formData.destinations.length <= 1) {
        return;
      }

      this.formData.destinations.splice(idx, 1);
      this.$delete(this.citiesByDestination, idx);
      this.$delete(this.loadingCitiesByDestination, idx);
      this.resetIndexedValidationErrors();
    },
    clearUryProjectData() {
      this.formData.serviceInPurchasePlan = '';
      this.formData.servicePurchasePlanJustification = '';
      this.formData.serviceGlosa = '';
      this.formData.serviceExpenseClassification = '';
      this.formData.serviceProjectObjective = '';
      this.formData.serviceResult = '';
      this.formData.serviceExpenseCode = '';
    },
    clearFlyingTicketData() {
      this.formData.flyingTicketBirthDate = '';
      this.formData.flyingTicketPhone = '';
      this.formData.flyingTicketInstitutionalEmail = '';
      this.formData.flyingTicketLuggage = '';
      this.formData.flyingTicketLuggageJustification = '';
      this.formData.flyingTicketDestinationDate = '';
      this.formData.flyingTicketDestinationInfo = '';
      this.formData.flyingTicketDestinationComments = '';
      this.formData.flyingTicketReturnDate = '';
      this.formData.flyingTicketReturnInfo = '';
      this.formData.flyingTicketReturnComments = '';
    },
    onDestinationTransferTypeChange() {
      // If the selected transport type is not personal/own vehicle, clear the plate.
      if (!this.shouldShowDestinationTransferPlate) {
        this.formData.destinationTransferPlate = '';
        // Clear possible VeeValidate error for this field
        if (this.$validator?.errors?.remove) {
          this.$validator.errors.remove('destinationTransferPlate');
        }
      }
    },
    onReturnTransferTypeChange() {
      // If the selected transport type is not personal/own vehicle, clear the plate.
      if (!this.shouldShowReturnTransferPlate) {
        this.formData.returnTransferPlate = '';
        // Clear possible VeeValidate error for this field
        if (this.$validator?.errors?.remove) {
          this.$validator.errors.remove('returnTransferPlate');
        }
      }
    },

    onRequesterLegalStatusChange() {
    if (!this.shouldShowRequesterGrade) {
      this.formData.requesterGrade = '';

      if (this.$validator?.errors?.remove) {
        this.$validator.errors.remove('requesterGrade');
      }

      return;
    }

    // Si es Planta / Contrata, restaurar grado
    if (!this.formData.requesterGrade) {
      this.formData.requesterGrade = this.getUserGrade || '';
    }
  },
    // Segment management methods
    createEmptySegment() {
      return {
        transferNumber: '',
        transferType: '',
        transferOtherDetail: '',
        transferPlate: '',
        transferDate: '',
        transferOrigin: '',
        transferDestination: '',
        refundApplies: false,
        justification: ''
      };
    },
    addOutboundSegment() {
      this.formData.outboundSegments.push(this.createEmptySegment());
    },
    addReturnSegment() {
      this.formData.returnSegments.push(this.createEmptySegment());
    },
    removeOutboundSegment(index) {
      if (this.formData.outboundSegments.length <= 1) {
        return;
      }

      this.formData.outboundSegments.splice(index, 1);
      this.$validator?.errors?.clear();
    },
    removeReturnSegment(index) {
      if (this.formData.returnSegments.length <= 1) {
        return;
      }

      this.formData.returnSegments.splice(index, 1);
      this.$validator?.errors?.clear();
    },
    initializeSegmentArrays() {
      // Initialize arrays if they don't exist (backward compatibility)
      if (!this.formData.outboundSegments) {
        this.formData.outboundSegments = [this.createEmptySegment()];
      }
      if (!this.formData.returnSegments) {
        this.formData.returnSegments = [this.createEmptySegment()];
      }

      // Ensure at least one segment exists in each array
      if (this.formData.outboundSegments.length === 0) {
        this.formData.outboundSegments.push(this.createEmptySegment());
      }
      if (this.formData.returnSegments.length === 0) {
        this.formData.returnSegments.push(this.createEmptySegment());
      }
    },
    migrateFromLegacyData() {
      // Migrate legacy single segment data to new array format if needed
      if (this.formData.destinationTransferNumber ||
          this.formData.destinationTransferType ||
          this.formData.destinationTransferDate ||
          this.formData.destinationTransferOrigin ||
          this.formData.destinationTransferDestination) {

        // Migrate outbound segment
        this.formData.outboundSegments[0] = {
          transferNumber: this.formData.destinationTransferNumber || '',
          transferType: this.formData.destinationTransferType || '',
          transferPlate: this.formData.destinationTransferPlate || '',
          transferDate: this.formData.destinationTransferDate || '',
          transferOrigin: this.formData.destinationTransferOrigin || '',
          transferDestination: this.formData.destinationTransferDestination || '',
          refundApplies: this.formData.destinationRefundApplies || false,
          justification: this.formData.destinationJustification || ''
        };
      }

      if (this.formData.returnTransferNumber ||
          this.formData.returnTransferType ||
          this.formData.returnTransferDate ||
          this.formData.returnTransferOrigin ||
          this.formData.returnTransferDestination) {

        // Migrate return segment
        this.formData.returnSegments[0] = {
          transferNumber: this.formData.returnTransferNumber || '',
          transferType: this.formData.returnTransferType || '',
          transferDate: this.formData.returnTransferDate || '',
          transferOrigin: this.formData.returnTransferOrigin || '',
          transferDestination: this.formData.returnTransferDestination || '',
          refundApplies: this.formData.returnRefundApplies || false,
          justification: this.formData.returnJustification || ''
        };
      }
    },

    shouldShowSegmentPlate(transferType) {
      const raw = transferType && typeof transferType === 'object'
        ? (transferType.label || transferType.value || '')
        : transferType;

      const type = (raw || '')
        .toString()
        .trim()
        .toLowerCase();

      return type === 'vehículo particular' || type === 'vehiculo particular';
    },
    getTransferTypeText(transferType) {
      const raw =
        transferType && typeof transferType === 'object'
          ? (transferType.label || transferType.value || '')
          : transferType;

      return (raw || '').toString().trim().toLowerCase();
    },

    isTransferTypeOther(transferType) {
      return this.getTransferTypeText(transferType) === 'otro';
    },

    handleSegmentTransferTypeChange(direction, value, index) {
      const key = direction === 'outbound' ? 'outboundSegments' : 'returnSegments';
      const segment = this.formData[key]?.[index];
      if (!segment) {
        return;
      }

      segment.transferType = value || '';

      const appliesPlate = this.shouldShowSegmentPlate(segment.transferType);
      const fieldName = `${direction}Segments[${index}].transferPlate`;

      if (!appliesPlate) {
        segment.transferPlate = '';

        this.$nextTick(() => {
          this.$validator?.errors?.remove(fieldName);
        });
      }

    },

    onOutboundSegmentTransferTypeChange(value, index) {
      this.handleSegmentTransferTypeChange('outbound', value, index);
    },

    onReturnSegmentTransferTypeChange(value, index) {
      this.handleSegmentTransferTypeChange('return', value, index);
    },

    async generatePdf(id) {
      showToast(this, 'Generando archivo PDF', 'En camino...');
      const response = await ioGetCustomDocument(this.$route.params.type, id, { pdf: 'si' });
      showToast(this, 'Archivo generado', 'Listo!', { variant: 'success', autoHideDelay: 3000 });
      window.open(response.data.pdf, '_blank');
      this.$router.push({ name: 'document-management' });
    },
    getMergedAttachments() {
      // Archivos manuales (origen "adjunto") que el usuario subió directamente
      const manualFiles = (this.formData.attachments || [])
        .filter((file) => file.origen === 'adjunto');
      // Archivos del DynamicFileSelector, excluyendo los de origen "adjunto"
      // porque esos se manejan exclusivamente por formData.attachments
      const selectorFiles = (this.formFiles || [])
        .filter((file) => file.origen !== 'adjunto');
      // Deduplicar por id
      const seen = new Set();
      return [...manualFiles, ...selectorFiles].filter((file) => {
        if (seen.has(file.id)) return false;
        seen.add(file.id);
        return true;
      });
    },
    async save(isDraft = false) {
      if (!await this.validateForm()) return;

      const from = this.$route.query?.from;

      this.saving = true;
      try {
        showToast(this, 'Guardando documento', 'En camino...', { variant: 'primary', autoHideDelay: 2000 });
        const payload = {
          type: this.$route.params.type,
          query: {
            ...this.$route.query,
            numerar: isDraft ? 'no' : 'si',
            ...(isDraft && { simular: 'si' }),
            ...(from === 'draft' && { replace: 'si' })
          },
          formData: {
            ...this.getFormData(),
            attachments: this.getMergedAttachments(),
            status: '1'
          },

        };

        const response = await ioCreateCustomDocument(payload);

        showToast(this, 'Documento creado correctamente', '¡Listo!', { variant: 'success', autoHideDelay: 5000 });

        try {
          this.notifyCreation(response.data.name || '').catch(() => {});
          await this.generatePdf(response.data.document.id);
        } catch (optionalError) {
          console.warn(optionalError);
        }

        this.$router.push({ name: 'document-management' });
      } catch (error) {
        this.saving = false;
      }
    },
    async saveDraft() {
      this.saving = true;
      const { from } = this.$route.query;
      try {
        showToast(this, 'Guardando documento', 'En camino...', { variant: 'primary', autoHideDelay: 2000 });
        const payload = {
          type: this.$route.params.type,
          query: {
            ...this.$route.query,
            numerar: 'no',
            ...(from === 'draft' && { replace: 'si' })
          },
          formData: {
            ...this.getFormData(),
            attachments: this.getMergedAttachments(),
            status: '3'
          },
        };

        const response = await ioCreateCustomDocument(payload);
        const docId = response.data?.document?.id || response.data?.documentId;
        if (docId) {
          await this.updateRouteAsDraft(docId);
        }
        showToast(this, 'Borrador guardado correctamente', '¡Listo!', { variant: 'success', autoHideDelay: 3000 });
      } finally {
        this.saving = false;
      }
    },
    async validateForm() {
      this.$validator.errors.clear();
      const validDynamicFileSelector = this.dynamicFileSelectorValid;
      // Validate all standard fields
      const fieldsValid = await this.$validator.validateAll();

      // Validate all outbound segments
      let segmentsValid = true;
      for (let i = 0; i < this.formData.outboundSegments.length; i++) {
        const segmentValid = await this.validateSegment('outbound', i);
        if (!segmentValid) {
          segmentsValid = false;
        }
      }

      // Validate all return segments
      for (let i = 0; i < this.formData.returnSegments.length; i++) {
        const segmentValid = await this.validateSegment('return', i);
        if (!segmentValid) {
          segmentsValid = false;
        }
      }

      const allValid = fieldsValid && segmentsValid && validDynamicFileSelector;

      if (!allValid) {
        showInvalidFormNotification(this);
      }
      return allValid;
    },
    async validateSegment(direction, index) {
      const segmentPrefix = direction === 'outbound' ? 'outboundSegments' : 'returnSegments';
      const segment = this.formData[`${direction}Segments`][index];

      const fieldNames = [
        `${segmentPrefix}[${index}].transferNumber`,
        `${segmentPrefix}[${index}].transferType`,
        `${segmentPrefix}[${index}].transferDate`,
        `${segmentPrefix}[${index}].transferOrigin`,
        `${segmentPrefix}[${index}].transferDestination`
      ];

      if (!segment.refundApplies) {
        fieldNames.push(`${segmentPrefix}[${index}].justification`);
      }

      let segmentValid = true;
      for (const fieldName of fieldNames) {
        if (!this.$validator.fields.find({ name: fieldName })) continue;
        const fieldValid = await this.$validator.validate(fieldName);
        if (!fieldValid) {
          segmentValid = false;
        }
      }

      return segmentValid;
    },
    async preview() {
      if (!await this.validateForm()) return;
      this.$refs.pdfPreview.setFormData(this.getFormData());
      await this.$refs.pdfPreview.preview();
    },
    async setRequesterData(fullUser) {
      this.formData.requesterName = fullUser.label || '';
      this.formData.requesterEmail = fullUser.correo || '';
      this.formData.requesterIut = fullUser.iut || '';
      await this.getWorkerData({
        tipoRespuesta: 2, // Hay 1 y 2
        elementoId: fullUser.id,
        referenciaId: this.company.id,
        filtros: `nombre:${Object.keys({ GRADO: null }).join()}`
      });
      this.formData.requesterGrade = this.getUserGrade;
    }
  },
  async mounted() {
    this.usr = this.$store.getters['Usuario/currentUser'];
    this.formData.requester = this.usr.id || '';
    this.formData.requesterEmail = this.usr.correo || '';
    this.formData.requesterName = this.usr.nombre || '';
    this.formData.requesterIut = this.usr.iut || '';
    this.formData.requestDate = getTodayDate();
    await this.fetchScfAttachmentsSelectors();
    await this.getWorkerData({
      tipoRespuesta: 2, // Hay 1 y 2
      elementoId: this.usr.id,
      referenciaId: this.company.id,
      filtros: `nombre:${Object.keys({ GRADO: null }).join()}`
    });
    this.formData.requesterGrade = this.getUserGrade;

    // ED-1867: asegurar inicialización y compatibilidad con campos legacy
    if (!this.formData.activities || !this.formData.activities.length) {
      this.formData.activities = [
        {
          startDate: this.formData.activityStarDate || '',
          endDate: this.formData.activityEndDate || '',
          name: this.formData.activityName || '',
          detail: this.formData.activityDetail || ''
        }
      ];
    } else {
      this.formData.activities[0].startDate =
        this.formData.activityStarDate || this.formData.activities[0].startDate || '';
      this.formData.activities[0].endDate =
        this.formData.activityEndDate || this.formData.activities[0].endDate || '';
      this.formData.activities[0].name =
        this.formData.activityName || this.formData.activities[0].name || '';
      this.formData.activities[0].detail =
        this.formData.activityDetail || this.formData.activities[0].detail || '';
    }

    // ED-1867: compatibilidad multi-period con campos legacy
    if (!this.formData.periods || !this.formData.periods.length) {
      this.formData.periods = [
        {
          startDate: this.formData.startDate || '',
          endDate: this.formData.endDate || ''
        }
      ];
    } else {
      this.formData.periods[0].startDate =
        this.formData.startDate || this.formData.periods[0].startDate || '';
      this.formData.periods[0].endDate =
        this.formData.endDate || this.formData.periods[0].endDate || '';
    }

    // ED-1867: compatibilidad multi-destination con campos legacy
    if (!this.formData.destinations || !this.formData.destinations.length) {
      this.formData.destinations = [
        {
          countryOfMission: this.formData.countryOfMission || '',
          countrySelected: this.formData.countrySelected || '',
          cityOfMission: this.formData.cityOfMission || '',
          citySelected: this.formData.citySelected || ''
        }
      ];
    } else {
      this.formData.destinations[0].countryOfMission =
        this.formData.countryOfMission || this.formData.destinations[0].countryOfMission || '';
      this.formData.destinations[0].countrySelected =
        this.formData.countrySelected || this.formData.destinations[0].countrySelected || '';
      this.formData.destinations[0].cityOfMission =
        this.formData.cityOfMission || this.formData.destinations[0].cityOfMission || '';
      this.formData.destinations[0].citySelected =
        this.formData.citySelected || this.formData.destinations[0].citySelected || '';
    }

    try {
      this.loadingCountries = true;
      const countries = await listV2Options({
        grupoOpcion: 'countries',
        itemsPorPagina: '20000',
        pagina: '1',
        filtros: 'padre:null|deshabilitado:0'
      });
      this.loadingCountries = false;
      if (countries) {
        this.countries = countries.data.opciones
          .map((item) => ({label: item.descripcion, value: item.opcionId}))
          .sort((a, b) => a.label.localeCompare(b.label));
      }
    } catch (e) {
      this.loadingCountries = false;
      console.log(e);
    }

    // Initialize segment arrays and migrate legacy data
    this.initializeSegmentArrays();
    this.migrateFromLegacyData();
  }
};
</script>
